import {
	styled,
	Stepper,
	Step,
	StepLabel,
	StepConnector,
	StepperProps,
	StepIconProps,
	Tooltip,
	StepLabelProps,
	StepConnectorProps,
	StepButton,
	StepButtonProps,
	createTheme,
	ThemeProvider,
	Skeleton,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import VerticalCheckIcon from "../images/vertical-stepper-checkmark.svg?react";
import Carrot from "../images/stepper-arrow.svg?react";
import i18nPackage from "../../i18n/i18n";
import { FONT_FAMILY } from "../../theme";
import styles from "./Stepper.module.css";

const coreStepperTheme = (vertical: boolean) =>
	createTheme({
		components: {
			MuiStepper: {
				styleOverrides: {
					root: {
						columnGap: "0px",
						display: "flex",
						justifyContent: vertical ? "flex-start" : "center",
						width: "100%",
						alignItems: "stretch",
						padding: vertical ? "20px 4px 30px 4px" : "initial",
						backgroundColor: vertical ? "#FBFBFB" : "#fff",
						height: vertical ? "100%" : "unset",
						gap: vertical ? "10px" : "25px",
						borderRadius: vertical ? "20px" : "initial",
						border: vertical ? "1px solid #e2e2e2" : "initial",
					},
				},
			},
			MuiStep: {
				styleOverrides: {
					root: {
						display: "flex",
						justifyContent: "center",
						alignItems: "flex-start",
						padding: 0,

						...(vertical && {
							'& > .MuiStepButton-root[aria-current="step"]': {
								"&, & .MuiStepLabel-root": {
									backgroundColor: "#F4F5FC",
									borderRadius: 8,
								},
							},
						}),
					},
				},
			},
			MuiStepButton: {
				styleOverrides: {
					root: {
						borderRadius: "4px",
						padding: vertical ? 0 : "6px",
						margin: 0,
						"&:hover": {
							backgroundColor: "#f5f5f5",
						},
						"&:focus-visible": {
							outline: "2px solid var(--focus-outline-color-dark)",
							outlineOffset: "2px",
						},
						"&:focus-visible:hover": {
							backgroundColor: "inherit",
						},
					},
				},
			},
			MuiStepLabel: {
				styleOverrides: {
					root: {
						display: "flex",
						justifyContent: vertical ? "flex-start" : "center",
						alignItems: "center",
						textAlign: vertical ? "left" : "center",
						padding: vertical ? "10px" : 0,
						width: "100%",
						gap: "10px !important",
					},
					label: {
						fontSize: "15px",
						transition: "color 100ms",
						textAlign: vertical ? "left" : "center",
						color: "#727272",
						fontWeight: "500",
						justifyContent: "flex-start",
						display: "flex",
						"&.Mui-completed": {
							color: "#727272",
							fontWeight: "500",
						},
						"&.Mui-active": {
							color: "#42296e",
							fontWeight: "600",
							...(vertical && {
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}),
						},
					},
					iconContainer: {
						padding: vertical ? "initial" : "0px",
					},
					alternativeLabel: {
						"& $label": {
							marginTop: "4px",
						},
					},
					labelContainer: {
						flexGrow: 1,
					},
				},
			},
			MuiStepIcon: {
				styleOverrides: {
					text: {
						fontFamily: FONT_FAMILY,
					},
				},
			},
			MuiStepConnector: {
				styleOverrides: {
					root: {
						margin: "8px 6px", // So focus outline of step doesn't overlap connector
						minWidth: "15px",
						alignSelf: "anchor-center",
					},
					line: {
						height: "1px",
						border: 0,
						backgroundColor: "#D3D3D3",
					},
				},
			},
		},
	});

interface CoreStepLabelProps extends StepLabelProps {
	usesTooltips?: boolean;
	active?: boolean;
	completed?: boolean;
	noWrap?: boolean;
	isSubstep?: boolean;
}

interface CoreStepConnectorProps extends StepConnectorProps {
	reducedConnectorWidth?: boolean;
}

const CoreStepLabel = styled(StepLabel, {
	shouldForwardProp: (prop) => !["usesTooltips", "noWrap", "isSubstep", "active", "completed"].includes(prop as string),
})<CoreStepLabelProps>((props) => ({
	gap: props.usesTooltips ? "0px" : "16px",
	marginRight: props.usesTooltips ? "10px" : undefined,
	marginLeft: props.usesTooltips ? "10px" : props.isSubstep ? "15px" : undefined,
	whiteSpace: props.noWrap ? "nowrap" : undefined,
}));

type StepLabelSkeletonProps = {
	/**Default: 130px */
	textWidth?: number;
	/**Default: 48px */
	textHeight?: number;
	/**Default: 28px */
	iconWidth?: number;
	/**Default: 28px */
	iconHeight?: number;
};

function StepLabelSkeleton({ textHeight, textWidth, iconHeight, iconWidth }: StepLabelSkeletonProps) {
	const finalTextWidth = `${textWidth ?? 130}px`;
	const finalTextHeight = `${textHeight ?? 48}px`;
	const finalIconWidth = `${iconWidth ?? 28}px`;
	const finalIconHeight = `${iconHeight ?? 28}px`;
	return (
		<CoreStepLabel
			completed={false}
			active={false}
			StepIconComponent={() => <Skeleton variant="circular" width={finalIconWidth} height={finalIconHeight} />}
		>
			<Skeleton variant="text" width={finalTextWidth} height={finalTextHeight} />
		</CoreStepLabel>
	);
}

const CoreStepperConnector = styled(StepConnector)<CoreStepConnectorProps>((props) => ({
	[`&`]: {
		flex: props.reducedConnectorWidth ? "0.3 1 0" : "0.5 1 0",
	},
}));

const CoreStepperStepIconRoot = styled("div")<{
	ownerState: { completed?: boolean; active?: boolean; vertical: boolean };
}>(({ ownerState }) => ({
	zIndex: 1,
	fontSize: "14px",
	fontWeight: "600",
	lineHeight: "0",
	width: 28,
	height: 28,
	display: "flex",
	borderRadius: "50%",
	justifyContent: "center",
	alignItems: "center",
	fontFamily: FONT_FAMILY,
	transition: "all 100ms",
	...(ownerState.active
		? {
				border: "1px solid #9288cb",
				backgroundColor: "#d4cfef",
				boxShadow: "inset 0 0 0 2px #fff",
				color: "#42296e",
				boxSizing: "border-box",
			}
		: ownerState.completed
			? { backgroundColor: "#27772D" }
			: { backgroundColor: "#ececf0" }),
}));

interface CoreStepIconProps extends StepIconProps {
	vertical: boolean;
	isSubstep: boolean;
	displayNumber: number;
}

const CoreStepIcon = ({ active, completed, displayNumber, className, vertical, isSubstep }: CoreStepIconProps) => {
	const showCompleteIcon = completed && !active;

	const width = isSubstep ? "24px" : "28px";
	const height = isSubstep ? "24px" : "28px";

	// UI team wants for SUBSTEPS, to only show the icon if it's complete
	if (isSubstep && !showCompleteIcon) {
		return <div style={{ width, height }} />;
	}

	return showCompleteIcon ? (
		<VerticalCheckIcon style={{ width: isSubstep ? "24px" : "28px", height: isSubstep ? "24px" : "28px" }}>
			<title>{i18nPackage.t("coreComponents.stepper.complete")}</title>
		</VerticalCheckIcon>
	) : (
		<CoreStepperStepIconRoot ownerState={{ completed, active, vertical }} className={className}>
			{displayNumber}
		</CoreStepperStepIconRoot>
	);
};

interface CoreStepButtonProps extends StepButtonProps {
	isActive?: boolean;
}

const CoreStepButton = ({ children, isActive, ...props }: CoreStepButtonProps) => (
	<StepButton
		disableRipple
		tabIndex={isActive ? -1 : 0}
		sx={{
			// UX team wants no styling for active step
			...(isActive && {
				pointerEvents: "none",
				"&:focus-visible": {
					outline: "none",
				},
				"&:hover": {
					backgroundColor: "inherit",
				},
			}),
		}}
		{...props}
	>
		{children}
	</StepButton>
);

export type StepConfig<T> = {
	key: T;
	label: string;
	parentSubstep?: T;
};

type CoreStepperProps<T extends string> = {
	variant?: "default" | "scrollable";
	steps: StepConfig<T>[];
	usesTooltips?: boolean;
	/**If provided, makes stepper non linear */
	onStepChange?: (step: number) => void;
	/**Used only in non linear mode. */
	completedSteps?: number[];
	/** Apply whiteSpace: nowrap to the CoreLabel */
	noWrapLabel?: boolean;
	vertical?: boolean;
	activeStep: number;
	loading?: boolean;
} & Omit<StepperProps, "connector" | "children" | "variant" | "activeStep">;

export function CoreStepper<T extends string>({
	variant = "default",
	steps,
	usesTooltips,
	onStepChange,
	completedSteps = [],
	noWrapLabel,
	vertical = false,
	activeStep,
	loading,
	...stepperProps
}: CoreStepperProps<T>) {
	const isNonLinear = typeof onStepChange === "function";
	const firstIncompleteIndex = steps.findIndex((_, index) => !completedSteps.includes(index));
	const lastAccessibleStep = firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex - 1;
	const stepperAriaLabel = i18nPackage.t("coreComponents.stepper.ariaLabel", { count: steps.length });

	const stepperRef = useRef<HTMLDivElement>(null);
	const [gradientOpacity, setGradientOpacity] = useState(0);
	const [expandedSteps, setExpandedSteps] = useState<Partial<Record<T, boolean>>>({});

	// Only parents should have numbers (according to UI team)
	const visibleNumberedSteps = steps.filter((s) => !s.parentSubstep);

	// key is the step key, and value is the 1-based index
	const stepNumberMap = Object.fromEntries(visibleNumberedSteps.map((s, i) => [s.key, i + 1]));

	useEffect(() => {
		// Get all current keys that are parents
		const parentKeys = new Set(steps.map((s) => s.parentSubstep).filter((key): key is T => !!key));

		setExpandedSteps((prev) => {
			const newState = { ...prev };
			let hasChanged = false;

			parentKeys.forEach((key) => {
				// ONLY initialize to false if a new parent was added
				// Need this otherwise everytime you expand/collapse, the entire UI updates
				if (newState[key] === undefined) {
					newState[key] = false;
					hasChanged = true;
				}
			});

			// only update state if a new parent was actually added
			return hasChanged ? newState : prev;
		});
	}, [steps]);

	useEffect(() => {
		const activeStepData = steps[activeStep];
		if (activeStepData?.parentSubstep) {
			// If the current step is a substep, force the parent to expand
			setExpandedSteps((prev) => ({
				...prev,
				[activeStepData.parentSubstep!]: true,
			}));
		}
	}, [activeStep]);

	useEffect(() => {
		if (variant !== "scrollable" || !stepperRef.current) return;
		const stepperCurrent = stepperRef.current;

		const updateGradient = () => {
			const maxScroll = stepperCurrent.scrollWidth - stepperCurrent.clientWidth;
			const scrollRemaining = maxScroll - stepperCurrent.scrollLeft;

			setGradientOpacity(Math.max(0, Math.min(scrollRemaining / 60, 1)));
		};

		stepperCurrent.addEventListener("scroll", updateGradient);
		updateGradient();

		return () => stepperCurrent.removeEventListener("scroll", updateGradient);
	}, [variant]);

	useEffect(() => {
		// timeout to let parent expand first
		const timer = setTimeout(() => {
			if (stepperRef.current) {
				// MUI adds 'Mui-active' to the active Step
				const activeElements = stepperRef.current.querySelectorAll(".Mui-active");
				// Ui team wants parent and substep to be "Active", so if there's 2 active styles, take the last one which is the substep
				const currentStepElement = activeElements[activeElements.length - 1];

				if (currentStepElement) {
					currentStepElement.scrollIntoView({
						behavior: "smooth",
						block: "nearest", // 'nearest' prevents the whole page from jumping
					});
				}
			}
		}, 100);

		return () => clearTimeout(timer);
	}, [activeStep, expandedSteps]); // Re-run when step changes or parents expand/collapse

	const handleToggleExpand = (e: React.MouseEvent, stepKey: T) => {
		e.stopPropagation();
		setExpandedSteps((prev) => ({
			...prev,
			[stepKey]: !prev[stepKey],
		}));
	};

	const stepperContent = (
		<ThemeProvider theme={coreStepperTheme(vertical)}>
			<Stepper
				{...stepperProps}
				ref={stepperRef}
				connector={vertical ? null : <CoreStepperConnector reducedConnectorWidth={usesTooltips} />}
				aria-label={stepperAriaLabel}
				nonLinear={isNonLinear}
				role="list"
				orientation={vertical ? "vertical" : "horizontal"}
			>
				{steps.map((step, index) => {
					const isSubstep = !!step.parentSubstep;

					// If it's a substep, check if its parent is expanded
					const isParentExpanded = step.parentSubstep ? !!expandedSteps[step.parentSubstep] : true;

					// If it's a substep and the parent is closed, don't render it
					if (isSubstep && !isParentExpanded) return null;

					// Determine if we should show the toggle arrow
					const isParent = steps.some((s) => s.parentSubstep === step.key);
					const isAnyChildActive = isParent ? steps[activeStep]?.parentSubstep === step.key : false;
					const amIExpanded = !!expandedSteps[step.key];

					const displayedLabel = step.label;
					const isActive = index === activeStep;
					const isCompleted = completedSteps.includes(index); // Only for non linearity
					const isAccessible = isCompleted && index <= lastAccessibleStep;
					const isNextStep = index === lastAccessibleStep + 1;
					const isDisabled = isNonLinear ? !(isActive || isAccessible || isNextStep) : true;

					// For substeps, UI team wants the parent AND selected child to be "active" visually
					const shouldShowActiveStyle = isActive || (isParent && isAnyChildActive);

					const displayStepNumber = isSubstep ? stepNumberMap[step.parentSubstep!] : stepNumberMap[step.key];

					const stepLabel = (
						<CoreStepLabel
							active={isActive}
							completed={isCompleted}
							StepIconComponent={(props: StepIconProps) => (
								<CoreStepIcon {...props} vertical={vertical} isSubstep={isSubstep} displayNumber={displayStepNumber} />
							)}
							usesTooltips={usesTooltips}
							noWrap={noWrapLabel}
							isSubstep={isSubstep}
						>
							<div className={`${styles.stepArrowWrapper} ${amIExpanded ? styles.expanded : ""}`}>
								{!usesTooltips && displayedLabel}
								{isParent && <Carrot />}
							</div>
						</CoreStepLabel>
					);

					const stepLabelWithButton = isNonLinear ? (
						<CoreStepButton
							onClick={(e) => {
								// For now clicking on parent doesnt do anything but expand or collapse because accessibility
								if (isParent) {
									handleToggleExpand(e, step.key);
								} else {
									onStepChange?.(index);
								}
							}}
							disabled={isDisabled || loading}
							aria-current={isActive ? "step" : undefined}
							aria-label={i18nPackage.t("coreComponents.stepper.stepAriaLabel", {
								index: index + 1,
								count: steps.length,
								status: isActive
									? i18nPackage.t("coreComponents.stepper.current")
									: isCompleted
										? i18nPackage.t("coreComponents.stepper.completed")
										: i18nPackage.t("coreComponents.stepper.incomplete"),
								label: displayedLabel,
							})}
							isActive={isActive && !loading}
							aria-expanded={isParent ? amIExpanded : undefined}
						>
							{stepLabel}
						</CoreStepButton>
					) : (
						stepLabel
					);

					const labelContent = usesTooltips ? (
						<Tooltip title={displayedLabel} placement="top">
							{stepLabelWithButton}
						</Tooltip>
					) : (
						stepLabelWithButton
					);

					return (
						<Step
							key={step.key}
							active={shouldShowActiveStyle && !loading}
							role="listitem"
							{...(isNonLinear && !loading && { completed: isCompleted })} // Add completed prop if in nonLinear. linear stepper tracks complete status automatically
						>
							{loading ? <StepLabelSkeleton /> : labelContent}
						</Step>
					);
				})}
			</Stepper>
		</ThemeProvider>
	);

	if (variant === "scrollable")
		return (
			<div className={styles.stepperWrapper}>
				<div className={styles.stepper}>{stepperContent}</div>
				<div className={styles.scrollGradient} style={{ opacity: gradientOpacity }} />
			</div>
		);

	return stepperContent;
}

type CoreVerticalStepperWrapperProps = {
	label: React.ReactNode;
	children: React.ReactNode;
	className?: string;
};

export function CoreVerticalStepperWrapper({ children, label, className }: CoreVerticalStepperWrapperProps) {
	return (
		<div className={`${styles.verticalStepperWrapper} ${className ? className : ""}`} tabIndex={0}>
			{label}
			{children}
		</div>
	);
}
